/**
 * Algolia client + small set of shared helpers used by both the search results
 * page (#41) and the predictive search overlay (#42).
 *
 * Loaded as a regular asset (non-module) so it runs in the global scope. The
 * file relies on `algoliasearch` (the lite UMD build) having been loaded
 * earlier in the page; both consumers gate that script include behind a
 * `template.name == 'search'` / overlay-mounted check in `theme.liquid`.
 *
 * Public API (all hung off `window.RTLAlgolia`):
 *   - createSearchClient({ applicationId, searchApiKey })  → algoliasearch client
 *   - getConfig()                                          → { applicationId, searchApiKey, productsIndexPrefix, hitsPerPage, productsIndex, collectionsIndex, suggestionsIndex, replicas, currency, currencyCode, locale, searchUrl }
 *   - normaliseSearchParams(searchString)                  → { query: string, tab: 'products'|'collections'|null, raw: URLSearchParams }
 *   - replaceQueryParam(name, value)                       → push canonical URL state without reload
 *   - formatPrice(amount)                                  → '$24.95' (uses theme currency)
 *   - escapeHtml(str)                                      → HTML-safe string for templating
 *   - hitProductUrl(hit)                                   → /products/<handle> (locale-aware)
 *   - hitCollectionUrl(hit)                                → /collections/<handle> (locale-aware)
 *
 * The module is defensive — it never throws if `window.theme.algolia` is
 * absent (the call site decides what to do); it just returns sensible
 * defaults so a misconfigured store does not crash the page.
 */
(function () {
  'use strict';

  var DEFAULT_CONFIG = {
    applicationId: '',
    searchApiKey: '',
    productsIndexPrefix: 'au',
    hitsPerPage: 24,
    enableCollectionsTab: true,
  };

  // Memoised config — computed on first read, invalidated by `resetConfigCache()`
  // (currently only the test harness needs the reset hook). Production reads
  // hit hot paths like `formatPrice` on every product hit (24 hits per page),
  // so caching saves the prefix/replica string concat on every call.
  var _configCache = null;

  function readThemeConfig() {
    if (_configCache) return _configCache;

    var fromWindow =
      window.theme && window.theme.algolia ? window.theme.algolia : {};
    var shop = window.theme && window.theme.shop ? window.theme.shop : {};
    var routes = window.theme && window.theme.routes ? window.theme.routes : {};

    var merged = Object.assign({}, DEFAULT_CONFIG, fromWindow);
    var prefix = merged.productsIndexPrefix || 'au';

    _configCache = {
      applicationId: merged.applicationId,
      searchApiKey: merged.searchApiKey,
      productsIndexPrefix: prefix,
      hitsPerPage: parseInt(merged.hitsPerPage, 10) || 24,
      enableCollectionsTab: merged.enableCollectionsTab !== false,
      productsIndex: prefix + '_products',
      collectionsIndex: prefix + '_collections',
      suggestionsIndex: prefix + '_products_query_suggestions',
      replicas: {
        featured: prefix + '_products',
        price_asc: prefix + '_products_price_asc',
        price_desc: prefix + '_products_price_desc',
        newest: prefix + '_products_newest',
        best_selling: prefix + '_products_best_selling',
      },
      currency: shop.currencySymbol || '$',
      currencyCode: shop.currencyCode || 'AUD',
      locale: shop.locale || 'en-AU',
      searchUrl: routes.search_url || '/search',
    };
    return _configCache;
  }

  function resetConfigCache() {
    _configCache = null;
  }

  /**
   * Build an algoliasearch lite client. Returns null if the UMD bundle has
   * not been loaded — both consumers should defer their init until the
   * algoliasearch global is available.
   */
  function createSearchClient(overrides) {
    var cfg = Object.assign({}, readThemeConfig(), overrides || {});
    if (
      typeof window.algoliasearch === 'undefined' &&
      typeof window.algoliasearchLite === 'undefined'
    ) {
      return null;
    }
    var factory =
      (typeof window.algoliasearch !== 'undefined' &&
        (window.algoliasearch.liteClient ||
          window.algoliasearch.searchClient ||
          window.algoliasearch)) ||
      window.algoliasearchLite;
    if (typeof factory !== 'function') return null;
    return factory(cfg.applicationId, cfg.searchApiKey);
  }

  /**
   * Read both `?q=` (Shopify-native) and `?query=` (legacy headless) from a
   * URLSearchParams instance. Returns the first non-empty match. Also surfaces
   * an optional `tab` parameter so the results page can persist the active
   * tab in the URL.
   */
  function normaliseSearchParams(searchString) {
    var raw = new URLSearchParams(
      typeof searchString === 'string' ? searchString : window.location.search
    );
    var query = raw.get('q');
    if (!query) query = raw.get('query');
    if (!query) query = '';
    var tab = raw.get('tab');
    if (tab !== 'products' && tab !== 'collections') tab = null;
    return { query: query, tab: tab, raw: raw };
  }

  /**
   * Replace (or remove, when value is empty) a single URL parameter and push
   * the result into `history.replaceState` so we never trigger a reload. The
   * canonical search URL uses `?q=`; this helper deliberately strips the
   * legacy `?query=` if present after normalisation.
   */
  function replaceQueryParam(name, value) {
    var url = new URL(window.location.href);
    var params = url.searchParams;
    if (name === 'q') {
      params.delete('query');
    }
    if (value === null || value === undefined || value === '') {
      params.delete(name);
    } else {
      params.set(name, value);
    }
    url.search = params.toString();
    try {
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (err) {
      /* Silently swallow — non-fatal */
    }
  }

  /**
   * Format a numeric Algolia `price` field using the shop currency.
   * Falls back to a simple `${symbol}${value}` if Intl is unavailable.
   */
  function formatPrice(amount) {
    var cfg = readThemeConfig();
    var numeric = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numeric)) return '';
    try {
      return new Intl.NumberFormat(cfg.locale, {
        style: 'currency',
        currency: cfg.currencyCode,
      }).format(numeric);
    } catch (err) {
      return cfg.currency + numeric.toFixed(2);
    }
  }

  function escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Validate a candidate hit URL — only http(s) absolute URLs and same-origin
   * relative URLs are allowed. Reject `javascript:`, `data:`, `vbscript:`, etc.
   * Returns the original URL when safe, otherwise `'#'`. The Algolia hit
   * payload is merchant-controlled today but the predictive overlay (#42) will
   * surface query-suggestion records too, so guard at the helper layer rather
   * than at each call site.
   */
  function sanitiseHitUrl(url) {
    if (typeof url !== 'string' || !url) return '#';
    // Relative path / hash / query — always safe (same-origin).
    if (
      url.charAt(0) === '/' ||
      url.charAt(0) === '#' ||
      url.charAt(0) === '?'
    ) {
      return url;
    }
    // Absolute URL — only http(s) allowed.
    if (/^https?:\/\//i.test(url)) return url;
    return '#';
  }

  function hitProductUrl(hit) {
    if (!hit) return '#';
    if (hit.url) return sanitiseHitUrl(hit.url);
    if (hit.handle) return '/products/' + encodeURIComponent(hit.handle);
    return '#';
  }

  function hitCollectionUrl(hit) {
    if (!hit) return '#';
    if (hit.url) return sanitiseHitUrl(hit.url);
    if (hit.handle) return '/collections/' + encodeURIComponent(hit.handle);
    return '#';
  }

  /**
   * Resolve once both the `algoliasearch` UMD bundle and the `instantsearch`
   * UMD bundle are present on `window`. The bundles are loaded via `<script
   * defer>` in `theme.liquid`, but Shopify bundles section `{% javascript %}`
   * blocks into `section.search.js` which is referenced lower in the document
   * and can execute ahead of the head-defer queue. Callers `await
   * RTLAlgolia.ready()` before mounting so the controller never races the
   * dependency load order.
   *
   * Resolves with `{ algoliasearch, instantsearch }` once both are present.
   * Polls on `requestAnimationFrame` (sub-16ms granularity) and gives up after
   * ~10s — at which point the page falls back to the no-JS shell rather than
   * spin forever.
   */
  function ready() {
    if (
      typeof window.algoliasearch !== 'undefined' &&
      typeof window.instantsearch === 'function'
    ) {
      return Promise.resolve({
        algoliasearch: window.algoliasearch,
        instantsearch: window.instantsearch,
      });
    }
    return new Promise(function (resolve, reject) {
      var started = Date.now();
      var TIMEOUT_MS = 10000;

      function tick() {
        if (
          typeof window.algoliasearch !== 'undefined' &&
          typeof window.instantsearch === 'function'
        ) {
          resolve({
            algoliasearch: window.algoliasearch,
            instantsearch: window.instantsearch,
          });
          return;
        }
        if (Date.now() - started > TIMEOUT_MS) {
          reject(
            new Error(
              'RTLAlgolia.ready: algoliasearch/instantsearch globals not ' +
                'present after ' +
                TIMEOUT_MS +
                'ms'
            )
          );
          return;
        }
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(tick);
        } else {
          setTimeout(tick, 16);
        }
      }
      tick();
    });
  }

  window.RTLAlgolia = {
    createSearchClient: createSearchClient,
    getConfig: readThemeConfig,
    resetConfigCache: resetConfigCache,
    normaliseSearchParams: normaliseSearchParams,
    replaceQueryParam: replaceQueryParam,
    formatPrice: formatPrice,
    escapeHtml: escapeHtml,
    sanitiseHitUrl: sanitiseHitUrl,
    hitProductUrl: hitProductUrl,
    hitCollectionUrl: hitCollectionUrl,
    ready: ready,
  };
})();
